import appConfig from '@/features/app/constants/app-config';
import staleQueryIfNotError from '@/features/app/functions/staleQueryIfNotError';
import { EnhancedUseQueryOptions } from '@/shared/types/enhanced-use-query-options';
import { useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

/**
 * Helper hook to create useQuery options with utility functions
 * @param queryOptions - The query options to use
 * @returns The query options with extra utility functions
 */
export default function useEnhancedQueryOptions<T>(
  queryOptions: UseQueryOptions<T>
): EnhancedUseQueryOptions<T> {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();

  return {
    isEnhancedUseQueryOptions: true,
    queryOptions: {
      ...queryOptions,
      queryKey: [...(queryOptions.queryKey ?? []), i18n.language],
      ...(queryOptions.staleTime
        ? {
            staleTime: queryOptions.staleTime,
          }
        : staleQueryIfNotError(appConfig.defaultDataStaleTime)),
    } satisfies UseQueryOptions<T>,
    prefetch: () => queryClient.prefetchQuery(queryOptions),
    fetch: () => queryClient.fetchQuery(queryOptions),
  };
}
